(() => {
  const videoObservers = new WeakMap();

  function initNavigation(nav) {
    if (!nav || nav.dataset.edbInitialized === 'true') return;
    nav.dataset.edbInitialized = 'true';
    const links = [...nav.querySelectorAll('a[href^="#"]')];
    const targets = links.map((link) => document.querySelector(link.getAttribute('href')));

    function setActive(index) {
      links.forEach((link, i) => {
        if (i === index) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    function updateActive() {
      if (!nav.isConnected) return;
      const threshold = nav.getBoundingClientRect().bottom + 28;
      let active = 0;
      targets.forEach((target, i) => {
        if (target && target.getBoundingClientRect().top <= threshold) active = i;
      });
      setActive(active);
    }

    links.forEach((link, i) => link.addEventListener('click', () => setActive(i)));
    addEventListener('scroll', updateActive, { passive: true });
    addEventListener('resize', updateActive);
    updateActive();
  }

  function initAmbientVideos(root) {
    if (!root) return;
    root.querySelectorAll('video.edb-page__ambient-video').forEach((video) => {
      if (video.dataset.edbInitialized === 'true') return;
      video.dataset.edbInitialized = 'true';
      video.muted = true;
      video.controls = false;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!('IntersectionObserver' in window)) {
        video.play().catch(() => {});
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && video.getClientRects().length) video.play().catch(() => {});
          else video.pause();
        });
      }, { rootMargin: '300px 0px', threshold: 0.01 });
      observer.observe(video);
      videoObservers.set(video, observer);
    });
  }

  function initDesign(design) {
    if (!design || design.dataset.edbInitialized === 'true') return;
    design.dataset.edbInitialized = 'true';
    const track = design.querySelector('[data-edb-design-track]');
    const cards = [...track.querySelectorAll('.edb-page__design-card')];
    const dots = [...design.querySelectorAll('[data-edb-design-dot]')];
    const previous = design.querySelector('[data-edb-design-previous]');
    const next = design.querySelector('[data-edb-design-next]');
    if (!cards.length) {
      previous.disabled = true;
      next.disabled = true;
      return;
    }
    let active = 0;

    function updateDesign(index) {
      active = Math.max(0, Math.min(cards.length - 1, index));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === active)));
      previous.disabled = active === 0;
      next.disabled = active === cards.length - 1;
    }

    function goToDesign(index) {
      const target = Math.max(0, Math.min(cards.length - 1, index));
      track.scrollTo({ left: cards[target].offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
      updateDesign(target);
    }

    previous.addEventListener('click', () => goToDesign(active - 1));
    next.addEventListener('click', () => goToDesign(active + 1));
    dots.forEach((dot, i) => dot.addEventListener('click', () => goToDesign(i)));
    track.addEventListener('scroll', () => {
      const nearest = cards.reduce((best, card, i) =>
        Math.abs(card.offsetLeft - cards[0].offsetLeft - track.scrollLeft) <
        Math.abs(cards[best].offsetLeft - cards[0].offsetLeft - track.scrollLeft) ? i : best, 0);
      updateDesign(nearest);
    }, { passive: true });
  }

  function initProduct(page) {
  if (!page || page.dataset.edbInitialized === 'true') return;
  page.dataset.edbInitialized = 'true';
  const hero = page.querySelector('[data-edb-hero]');
  const slides = [...page.querySelectorAll('[data-edb-slide]')];
  const thumbnails = [...page.querySelectorAll('[data-edb-thumb]')];
  const variants = [...page.querySelectorAll('[data-edb-variant]')];
  const form = page.querySelector('.edb-page__form');
  const quantity = page.querySelector('[data-edb-quantity]');
  const addButton = page.querySelector('[data-edb-add]');
  const buyButton = page.querySelector('[name="checkout"]');
  const sticky = page.querySelector('[data-edb-sticky]');
  const message = page.querySelector('[data-edb-message]');
  const warrantyTrigger = page.querySelector('[data-edb-warranty-trigger]');
  const warrantyDialog = page.querySelector('[data-edb-warranty-dialog]');
  let color = page.dataset.initialColor;
  let slideIndex = 0;
  const visibleSlides = () => slides.filter((slide) => slide.dataset.color === color);

  function showSlide(index) {
    const current = visibleSlides();
    if (!current.length) return;
    slideIndex = (index + current.length) % current.length;
    const selected = current[slideIndex];
    hero.src = selected.dataset.src;
    hero.alt = selected.dataset.alt;
    slides.forEach((slide) => {
      slide.hidden = slide.dataset.color !== color;
      slide.setAttribute('aria-current', String(slide === selected));
    });
    thumbnails.forEach((thumbnail) => {
      thumbnail.hidden = thumbnail.dataset.color !== color;
      thumbnail.setAttribute('aria-pressed', String(thumbnail.dataset.color === color && Number(thumbnail.dataset.index) === slideIndex));
    });
  }

  function selectVariant(button, updateUrl = true) {
    color = button.dataset.color;
    variants.forEach((variant) => variant.setAttribute('aria-pressed', String(variant === button)));
    form.querySelector('[data-edb-variant-id]').value = button.dataset.id;
    page.querySelector('[data-edb-color]').textContent = color;
    page.querySelector('[data-edb-price]').textContent = button.dataset.price;
    const comparePrice = button.dataset.comparePrice || '';
    page.querySelectorAll('[data-edb-compare-price], [data-edb-sticky-compare-price]').forEach((element) => {
      element.textContent = comparePrice;
      element.hidden = !comparePrice;
    });
    const savings = button.dataset.saveAmount || '';
    const savingBadge = page.querySelector('[data-edb-saving]');
    savingBadge.textContent = savings ? `SAVE ${savings}` : '';
    savingBadge.hidden = !savings;
    page.querySelector('[data-edb-sticky-color]').textContent = color;
    page.querySelector('[data-edb-sticky-price]').textContent = button.dataset.price;
    const available = button.dataset.available === 'true';
    page.querySelector('[data-edb-stock]').textContent = available ? 'In Stock' : 'Sold Out';
    addButton.disabled = !available;
    buyButton.disabled = !available;
    sticky.querySelector('button').disabled = !available;
    addButton.textContent = available ? 'Add to cart' : 'Sold Out';
    quantity.max = button.dataset.quantity;
    quantity.value = '1';
    message.textContent = '';
    showSlide(0);
    page.querySelector('[data-edb-sticky-image]').src = hero.src;
    if (updateUrl) {
      const url = new URL(location.href);
      url.searchParams.set('variant', button.dataset.id);
      history.replaceState({}, '', url);
    }
  }

  function normalizeQuantity(value) {
    const maximum = Number(quantity.max) || 1;
    return Math.min(maximum, Math.max(1, Number.parseInt(value, 10) || 1));
  }

  page.querySelector('[data-edb-previous]').addEventListener('click', () => showSlide(slideIndex - 1));
  page.querySelector('[data-edb-next]').addEventListener('click', () => showSlide(slideIndex + 1));
  slides.forEach((slide) => slide.addEventListener('click', () => showSlide(visibleSlides().indexOf(slide))));
  thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', () => showSlide(Number(thumbnail.dataset.index))));
  variants.forEach((variant) => variant.addEventListener('click', () => selectVariant(variant)));
  page.querySelector('[data-edb-decrease]').addEventListener('click', () => { quantity.value = normalizeQuantity(Number(quantity.value) - 1); });
  page.querySelector('[data-edb-increase]').addEventListener('click', () => { quantity.value = normalizeQuantity(Number(quantity.value) + 1); });
  quantity.addEventListener('change', () => { quantity.value = normalizeQuantity(quantity.value); });
  sticky.querySelector('button').addEventListener('click', () => form.requestSubmit(addButton));

  if (warrantyTrigger && warrantyDialog) {
    warrantyTrigger.addEventListener('click', () => warrantyDialog.showModal());
    warrantyDialog.querySelector('[data-edb-warranty-close]').addEventListener('click', () => warrantyDialog.close());
    warrantyDialog.addEventListener('click', (event) => {
      if (event.target !== warrantyDialog) return;
      const bounds = warrantyDialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) warrantyDialog.close();
    });
    warrantyDialog.addEventListener('close', () => warrantyTrigger.focus());
  }

  form.addEventListener('submit', async (event) => {
    quantity.value = normalizeQuantity(quantity.value);
    const checkoutRequested = event.submitter === buyButton;
    event.preventDefault();
    addButton.disabled = true;
    buyButton.disabled = true;
    message.textContent = '';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Could not add this item to the cart.');
      location.assign(`${root}${checkoutRequested ? 'checkout' : 'cart'}`);
    } catch (error) {
      message.textContent = error.message || 'Could not add this item to the cart.';
      addButton.disabled = false;
      buyButton.disabled = false;
    }
  });

  const footer = document.querySelector('footer');
  function updateSticky() {
    const passedForm = form.getBoundingClientRect().bottom < 0;
    const footerVisible = footer && footer.getBoundingClientRect().top < innerHeight;
    sticky.dataset.visible = String(passedForm && !footerVisible);
  }
  addEventListener('scroll', updateSticky, { passive: true });
  addEventListener('resize', updateSticky);
  updateSticky();
  showSlide(0);
  }

  initProduct(document.querySelector('[data-edb-product]'));
  initDesign(document.querySelector('[data-edb-design]'));
  initNavigation(document.querySelector('[data-edb-nav]'));
  initAmbientVideos(document);
  document.addEventListener('shopify:section:load', (event) => {
    initProduct(event.target.querySelector('[data-edb-product]'));
    initDesign(event.target.querySelector('[data-edb-design]'));
    initNavigation(event.target.querySelector('[data-edb-nav]'));
    initAmbientVideos(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('video.edb-page__ambient-video').forEach((video) => {
      videoObservers.get(video)?.disconnect();
      video.pause();
    });
  });
})();
